import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * TimePicker — 24h hour + minute Select pair, replacing `<input type="time">`.
 *
 * Value/onChange round-trips `HH:mm` strings (or `null` when blank).
 * Minute step defaults to 15 for ergonomic ETA selection but is configurable.
 */
export function TimePicker({
  value,
  onChange,
  disabled,
  minuteStep = 15,
  className,
  invalid,
}: {
  value: string | null;
  onChange: (hhmm: string | null) => void;
  disabled?: boolean;
  minuteStep?: number;
  className?: string;
  invalid?: boolean;
}) {
  const [h, m] = parseHHMM(value);
  const hours = Array.from({ length: 24 }, (_, i) => pad(i));
  const minutes = Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => pad(i * minuteStep));

  function setHour(next: string) {
    onChange(`${next}:${pad(m ?? 0)}`);
  }
  function setMinute(next: string) {
    onChange(`${pad(h ?? 12)}:${next}`);
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Clock className="h-4 w-4 text-muted-foreground" />
      <Select value={h != null ? pad(h) : ""} onValueChange={setHour} disabled={disabled}>
        <SelectTrigger
          className={cn("w-[5.5rem]", invalid && "border-destructive focus:ring-destructive")}
          aria-label="Hour"
        >
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {hours.map((hh) => (
            <SelectItem key={hh} value={hh}>{hh}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">:</span>
      <Select value={m != null ? pad(m) : ""} onValueChange={setMinute} disabled={disabled}>
        <SelectTrigger
          className={cn("w-[5.5rem]", invalid && "border-destructive focus:ring-destructive")}
          aria-label="Minute"
        >
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {minutes.map((mm) => (
            <SelectItem key={mm} value={mm}>{mm}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value && !disabled && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-muted-foreground hover:text-foreground underline cursor-pointer"
        >
          Clear
        </button>
      )}
    </div>
  );
}

function parseHHMM(value: string | null): [number | null, number | null] {
  if (!value) return [null, null];
  const match = /^(\d{1,2}):(\d{1,2})$/.exec(value);
  if (!match) return [null, null];
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return [null, null];
  if (h < 0 || h > 23 || m < 0 || m > 59) return [null, null];
  return [h, m];
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
